import React, { useState, useRef, useEffect } from 'react';
import { UserPlus, Check, Phone, User, FileText, Loader2, Lock, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast, Toaster } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuickAddContact = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    summary: ''
  });
  const [saving, setSaving] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const nameInputRef = useRef(null);

  // Set up PWA manifest and handle install prompt
  useEffect(() => {
    // Change manifest to quick-specific one
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/manifest-quick.json';
    }
    
    // Update page title and theme
    document.title = 'Contato Rápido - CRM';
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
      themeColor.content = '#0f172a';
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      // Restore original manifest when leaving
      if (manifestLink) {
        manifestLink.href = '/manifest.json';
      }
    };
  }, []);

  // Handle PWA install
  const handleInstall = async () => {
    if (!installPrompt) {
      toast.info('Para instalar, use o menu do navegador (3 pontos) → "Instalar app"');
      return;
    }
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App instalado! Procure "Contato+" na sua área de trabalho');
      setIsInstalled(true);
    }
    setInstallPrompt(null);
  };

  // Check authentication on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('gelatti_admin_auth');
    const authTime = localStorage.getItem('gelatti_admin_time');
    
    if (authStatus === 'true' && authTime) {
      const elapsed = Date.now() - parseInt(authTime);
      const fourHours = 4 * 60 * 60 * 1000;
      if (elapsed < fourHours) {
        setIsAuthenticated(true);
      }
    }
    setChecking(false);
  }, []);

  // Focus name input when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      nameInputRef.current?.focus();
    }
  }, [isAuthenticated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error('Nome e telefone são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/crm/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          summary: form.summary.trim(),
          source: 'whatsapp',
          status: 'novo_lead'
        })
      });

      if (!response.ok) throw new Error('Erro ao salvar');

      const data = await response.json();
      setLastAdded({ name: form.name, phone: form.phone });
      toast.success(`${form.name} adicionado ao CRM!`);
      
      // Clear form and refocus
      setForm({ name: '', phone: '', summary: '' });
      nameInputRef.current?.focus();
      
      // Clear lastAdded after 3 seconds
      setTimeout(() => setLastAdded(null), 3000);
    } catch (error) {
      toast.error('Erro ao adicionar contato');
    } finally {
      setSaving(false);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit(e);
    }
  };

  // Loading state
  if (checking) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-white mb-2">Acesso Restrito</h1>
          <p className="text-slate-400 mb-6">Você precisa estar logado para adicionar contatos.</p>
          <a 
            href="/admin" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors"
          >
            Fazer Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Toaster position="top-center" richColors />
      
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
            <UserPlus className="w-6 h-6 text-teal-400" />
          </div>
          <h1 className="text-xl font-bold text-white">Contato Rápido</h1>
          <p className="text-sm text-slate-400 mt-1">Adicione ao CRM em segundos</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="space-y-3">
          {/* Name */}
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              ref={nameInputRef}
              value={form.name}
              onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Nome do contato"
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-12"
              autoComplete="off"
            />
          </div>

          {/* Phone */}
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={form.phone}
              onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
              placeholder="Telefone (cole do WhatsApp)"
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 h-12"
              autoComplete="off"
            />
          </div>

          {/* Summary */}
          <div className="relative">
            <FileText className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
            <Textarea
              value={form.summary}
              onChange={(e) => setForm(p => ({ ...p, summary: e.target.value }))}
              placeholder="Resumo rápido (opcional)"
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 min-h-[60px] resize-none"
              rows={2}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={saving || !form.name.trim() || !form.phone.trim()}
            className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white font-medium text-base"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Adicionar (Ctrl+Enter)
              </>
            )}
          </Button>
        </form>

        {/* Last Added Feedback */}
        {lastAdded && (
          <div className="mt-4 p-3 bg-teal-500/20 border border-teal-500/30 rounded-lg animate-in fade-in slide-in-from-bottom-2">
            <p className="text-teal-300 text-sm text-center">
              <Check className="w-4 h-4 inline mr-1" />
              <strong>{lastAdded.name}</strong> adicionado!
            </p>
          </div>
        )}

        {/* Tips */}
        <div className="mt-6 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <p className="text-xs text-slate-400 text-center">
            <strong className="text-slate-300">Dica:</strong> Copie o número no WhatsApp e cole aqui.
            <br />
            <span className="text-slate-500">Origem será salva como "WhatsApp"</span>
          </p>
        </div>

        {/* Install as PWA */}
        {!isInstalled && (
          <Button
            onClick={handleInstall}
            variant="outline"
            className="w-full mt-4 h-10 bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Instalar como App
          </Button>
        )}

        {/* Link to CRM */}
        <div className="mt-4 text-center">
          <a 
            href="/admin" 
            target="_blank"
            className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
          >
            Abrir CRM completo →
          </a>
        </div>
      </div>
    </div>
  );
};

export default QuickAddContact;
